import { Group } from '@vue-altoleap-libraries/vue-altoleap-accounts-lib';
import { AbstractModel } from '../base';
import {
  WorkflowTransitionActionTypeEnum,
  WorkflowStateAssignmentTypeEnum,
  WorkflowStateEnum,
  TransitionActionRecipientTypeEnum,
  TransitionActionRecipientSubTypeEnum,
  TransitionNodeBinaryOperators,
  TransitionNodeOperandSubtype
} from './workflow.constants';
import {
  IWorkflow,
  IWorkflowData,
  IWorkflowState,
  IWorkflowStateConfig,
  IWorkflowStateConfigData,
  IWorkflowStateData,
  IWorkflowTransition,
  IWorkflowTransitionNodeOperatorInfo,
  IWorkflowStateThreshold,
  IWorkflowStateThresholdData,
  IWorkflowTransitionNodeOperandTypeInfo,
  IWorkflowTransitionData,
  IWorkflowTransitionLog,
  IWorkflowTransitionLogData,
  IWorkflowTransitionActionEmailData,
  IWorkflowTransitionActionData,
  IWorkflowTransitionActionEmail,
  IWorkflowTransitionAction,
  IWorkflowTransitionActionEmailRecipient,
  IWorkflowTransitionActionEmailRecipientData,
  IWorkflowStateReminderConfig,
  IWorkflowStateReminderConfigData,
  IWorkflowStateReminderEmailData,
  IWorkflowStateReminderEmail,
  IWorkflowTransitionNode,
  IWorkflowTransitionNodeData,
  IWorkflowStateThresholdGroup,
  IWorkflowStateThresholdGroupData
} from './workflow.types';

import moment from 'moment-timezone';
import { padStart } from 'lodash';
import { AbstractPredicateNode } from '../predicate/predicate';
import {
  ExpressionPredicateNodeTree,
  convertLogicalExpressionTreeToPredicateNodeList,
  convertPredicateNodeListToLogicalExpressionTree
} from '../predicate/tree';

export class Workflow extends AbstractModel implements IWorkflow {
  public readonly id?: number;
  public readonly name: string;
  public readonly description: string;
  public readonly state: WorkflowStateEnum = WorkflowStateEnum.Draft;
  public readonly created?: Date;
  public readonly modified?: Date;
  public readonly owner?: string;

  constructor(data: IWorkflowData) {
    super();

    this.validate(data, ['name']);

    this.name = data.name!;

    this.description = data.description ? data.description : '';

    if (data.id) {
      this.id = data.id;
    }
    if (data.state) {
      this.state = data.state;
    }

    if (data.owner) {
      this.owner = data.owner;
    }

    if (data.created) {
      this.created =
        typeof data.created === 'string'
          ? new Date(data.created)
          : data.created;
    }

    if (data.modified) {
      this.modified =
        typeof data.modified === 'string'
          ? new Date(data.modified)
          : data.modified;
    }
  }

  get workflowId() {
    return `WF-${this.name}-${moment(this.created).format(
      'YYYY-MM-DD'
    )}-${padStart(this.id!.toString(), 4, '0')}`;
  }

  toString() {
    return this.name;
  }

  isPublished(): boolean {
    return this.state === WorkflowStateEnum.Published;
  }
}

export class WorkflowStateConfig extends AbstractModel
  implements IWorkflowStateConfig {
  public readonly id?: number;
  public readonly color: string;

  constructor(data: IWorkflowStateConfigData) {
    super();

    this.validate(data, ['color']);

    this.color = data.color!;

    if (data.id) {
      this.id = data.id;
    }
  }
}

export class WorkflowState extends AbstractModel implements IWorkflowState {
  public readonly id?: number;
  public readonly name: string;
  public readonly default: boolean = false;
  public readonly config: WorkflowStateConfig = new WorkflowStateConfig({
    color: '#103A79'
  });
  public readonly assignment_type: WorkflowStateAssignmentTypeEnum;
  public readonly groups: (Group | number)[] = [];
  public readonly is_locked: boolean = false;
  public readonly enable_reminder: boolean = false;
  public readonly reminder_config: IWorkflowStateReminderConfig | null = null;
  public readonly threshold_groups: IWorkflowStateThresholdGroup[] = [];

  constructor(data: IWorkflowStateData) {
    super();

    this.validate(data, ['name']);

    this.name = data.name!;

    if (data.id) {
      this.id = data.id;
    }

    if (data.default) {
      this.default = data.default;
    }

    if (data.config) {
      this.config = new WorkflowStateConfig(data.config);
    }

    if (data.groups) {
      this.groups = data.groups.map(group =>
        typeof group === 'number' ? group : new Group(group)
      );
    }

    this.assignment_type =
      data.assignment_type ?? WorkflowStateAssignmentTypeEnum.None;

    if (data.is_locked) {
      this.is_locked = data.is_locked;
    }

    if (data.enable_reminder) {
      this.enable_reminder = data.enable_reminder;
    }

    if (data.reminder_config) {
      this.reminder_config = new WorkflowStateReminderConfig(
        data.reminder_config
      );
    }

    if (data.threshold_groups) {
      this.threshold_groups = data.threshold_groups.map(
        group => new WorkflowStateThresholdGroup(group)
      );
    }
  }

  toString() {
    return this.name;
  }
}

export class WorkflowTransition extends AbstractModel
  implements IWorkflowTransition {
  public readonly id?: number;
  public readonly name: string;
  public readonly source_state: IWorkflowState | number;
  public readonly target_state: IWorkflowState | number;
  public readonly validation_required: boolean;
  public readonly comment_required: boolean;
  public readonly actions: IWorkflowTransitionAction[] = [];

  public readonly nodes: IWorkflowTransitionNode[] = [];

  constructor(data: IWorkflowTransitionData) {
    super();

    this.validate(data, ['name', 'source_state', 'target_state']);

    this.name = data.name!;
    this.source_state =
      typeof data.source_state === 'number'
        ? data.source_state
        : new WorkflowState(data.source_state!);
    this.target_state =
      typeof data.target_state === 'number'
        ? data.target_state
        : new WorkflowState(data.target_state!);

    if (data.id) {
      this.id = data.id;
    }

    if (data.actions) {
      this.actions = data.actions.map(
        action => new WorkflowTransitionAction(action)
      );
    }

    this.validation_required = data.validation_required ?? true;
    this.comment_required = data.comment_required ?? false;

    if (data.nodes) {
      this.nodes = data.nodes.map(node => new WorkflowTransitionNode(node));
    }
  }

  toString() {
    return this.name;
  }

  toExpressionTree(): ExpressionPredicateNodeTree<IWorkflowTransitionNode> {
    return convertPredicateNodeListToLogicalExpressionTree(this.nodes);
  }

  /**
   * Converts the current transition object to a string representation
   * following the UML (Unified Modeling Language) format for transitions.
   *
   * The resulting string includes:
   * - The base string representation of the transition.
   * - Any associated conditions, formatted as a list within square brackets,
   *   with each condition converted to its UML string representation.
   * - Any associated actions, formatted as a list following a slash '/',
   *   with each action represented by its name.
   */
  toTransitionString() {
    let str = this.toString().toUpperCase();

    if (this.nodes.length > 0) {
      str += `\n[${this.expressionTreeToString(this.toExpressionTree())}]`;
    }

    if (this.actions.length > 0) {
      str += `\n/${this.actions.map(action => action.name).join('')}`;
    }

    return str;
  }

  expressionTreeToString(
    tree: ExpressionPredicateNodeTree<IWorkflowTransitionNode>,
    depth = 0
  ): string {
    return tree
      .map(node => {
        if (Array.isArray(node)) {
          // Recursively process subgroups
          const expr = this.expressionTreeToString(node, depth + 1);
          return expr;
        } else {
          // Leaf node: use its toString method
          const expr = node.toString();
          return depth < 1 ? `${expr}\n` : expr;
        }
      })
      .join(' ');
  }
}

export class WorkflowStateThreshold extends AbstractModel
  implements IWorkflowStateThreshold {
  public readonly id?: number;
  public readonly group: Group | number;
  public readonly threshold: null | number;
  public readonly threshold_group:
    | IWorkflowStateThresholdGroup
    | number
    | null = null;

  constructor(data: IWorkflowStateThresholdData) {
    super();

    this.validate(data, ['group']);

    if (data.id) {
      this.id = data.id;
    }

    this.group =
      typeof data.group === 'number' ? data.group : new Group(data.group!);

    this.threshold = data.threshold ?? null;

    if (data.threshold_group) {
      this.threshold_group =
        typeof data.threshold_group === 'number'
          ? data.threshold_group
          : new WorkflowStateThresholdGroup(data.threshold_group);
    }
  }
}

export class WorkflowTransitionNodeOperatorInfo
  implements IWorkflowTransitionNodeOperatorInfo {
  readonly value: TransitionNodeBinaryOperators;
  readonly display_name: string;
  readonly icon: string;

  constructor(data: IWorkflowTransitionNodeOperatorInfo) {
    this.value = data.value;
    this.display_name = data.display_name;
    this.icon = data.icon;
  }

  static Equals = new WorkflowTransitionNodeOperatorInfo({
    display_name: 'Equals',
    value: TransitionNodeBinaryOperators.Equals,
    icon: 'mdi-equal'
  });

  static NotEqual = new WorkflowTransitionNodeOperatorInfo({
    display_name: 'Not equals',
    value: TransitionNodeBinaryOperators.NotEqual,
    icon: 'mdi-not-equal'
  });

  static GreaterThan = new WorkflowTransitionNodeOperatorInfo({
    display_name: 'Greater than',
    value: TransitionNodeBinaryOperators.GreaterThan,
    icon: 'mdi-greater-than'
  });

  static GreaterThanEqual = new WorkflowTransitionNodeOperatorInfo({
    display_name: 'Greater than or equals',
    value: TransitionNodeBinaryOperators.GreaterThanEqual,
    icon: 'mdi-greater-than-or-equal'
  });

  static LessThan = new WorkflowTransitionNodeOperatorInfo({
    display_name: 'Less than',
    value: TransitionNodeBinaryOperators.LessThan,
    icon: 'mdi-less-than'
  });

  static LessThanEqual = new WorkflowTransitionNodeOperatorInfo({
    display_name: 'Less than or equals',
    value: TransitionNodeBinaryOperators.LessThanEqual,
    icon: 'mdi-less-than-or-equal'
  });

  static getWorkflowTransitionNodeOperator(
    value: TransitionNodeBinaryOperators
  ): IWorkflowTransitionNodeOperatorInfo | null {
    let stateInfo = null;
    switch (value) {
      case TransitionNodeBinaryOperators.Equals:
        stateInfo = WorkflowTransitionNodeOperatorInfo.Equals;
        break;

      case TransitionNodeBinaryOperators.NotEqual:
        stateInfo = WorkflowTransitionNodeOperatorInfo.NotEqual;
        break;

      case TransitionNodeBinaryOperators.GreaterThan:
        stateInfo = WorkflowTransitionNodeOperatorInfo.GreaterThan;
        break;

      case TransitionNodeBinaryOperators.GreaterThanEqual:
        stateInfo = WorkflowTransitionNodeOperatorInfo.GreaterThanEqual;
        break;

      case TransitionNodeBinaryOperators.LessThan:
        stateInfo = WorkflowTransitionNodeOperatorInfo.LessThan;
        break;

      case TransitionNodeBinaryOperators.LessThanEqual:
        stateInfo = WorkflowTransitionNodeOperatorInfo.LessThanEqual;
        break;
    }

    return stateInfo;
  }

  static getWorkflowTransitionNodeOperatorInfoList(): IWorkflowTransitionNodeOperatorInfo[] {
    return [
      WorkflowTransitionNodeOperatorInfo.Equals,
      WorkflowTransitionNodeOperatorInfo.NotEqual,
      WorkflowTransitionNodeOperatorInfo.GreaterThan,
      WorkflowTransitionNodeOperatorInfo.GreaterThanEqual,
      WorkflowTransitionNodeOperatorInfo.LessThan,
      WorkflowTransitionNodeOperatorInfo.LessThanEqual
    ];
  }
}

export class WorkflowTransitionNodeOperandTypeInfo
  implements IWorkflowTransitionNodeOperandTypeInfo {
  readonly value: TransitionNodeOperandSubtype;
  readonly display_name: string;

  constructor(data: IWorkflowTransitionNodeOperandTypeInfo) {
    this.value = data.value;
    this.display_name = data.display_name;
  }

  static Constant = new WorkflowTransitionNodeOperandTypeInfo({
    display_name: 'Constant',
    value: TransitionNodeOperandSubtype.Constant
  });

  static Variable = new WorkflowTransitionNodeOperandTypeInfo({
    display_name: 'Property',
    value: TransitionNodeOperandSubtype.Variable
  });
  static OwnerGroup = new WorkflowTransitionNodeOperandTypeInfo({
    display_name: 'Owner Group',
    value: TransitionNodeOperandSubtype.OwnerGroup
  });
  static Threshold = new WorkflowTransitionNodeOperandTypeInfo({
    display_name: 'Threshold',
    value: TransitionNodeOperandSubtype.Threshold
  });
  static AssigneeGroup = new WorkflowTransitionNodeOperandTypeInfo({
    display_name: 'Assignee Group',
    value: TransitionNodeOperandSubtype.AssigneeGroup
  });

  static getWorkflowTransitionNodeOperandType(
    value: TransitionNodeOperandSubtype
  ): IWorkflowTransitionNodeOperandTypeInfo {
    let stateInfo: IWorkflowTransitionNodeOperandTypeInfo;
    switch (value) {
      case TransitionNodeOperandSubtype.Constant:
        stateInfo = WorkflowTransitionNodeOperandTypeInfo.Constant;
        break;

      case TransitionNodeOperandSubtype.Variable:
        stateInfo = WorkflowTransitionNodeOperandTypeInfo.Variable;
        break;

      case TransitionNodeOperandSubtype.OwnerGroup:
        stateInfo = WorkflowTransitionNodeOperandTypeInfo.OwnerGroup;
        break;

      case TransitionNodeOperandSubtype.Threshold:
        stateInfo = WorkflowTransitionNodeOperandTypeInfo.Threshold;
        break;

      case TransitionNodeOperandSubtype.AssigneeGroup:
        stateInfo = WorkflowTransitionNodeOperandTypeInfo.AssigneeGroup;
        break;
    }
    return stateInfo;
  }

  static getWorkflowTransitionNodeOperandTypeInfoList(): IWorkflowTransitionNodeOperandTypeInfo[] {
    return [
      WorkflowTransitionNodeOperandTypeInfo.Variable,
      WorkflowTransitionNodeOperandTypeInfo.OwnerGroup,
      WorkflowTransitionNodeOperandTypeInfo.AssigneeGroup,
      WorkflowTransitionNodeOperandTypeInfo.Threshold
    ];
  }
}

export class WorkflowTransitionLog extends AbstractModel
  implements IWorkflowTransitionLog {
  readonly id: number;
  readonly transition: IWorkflowTransition;
  readonly user: string;
  readonly notes: string;
  readonly created?: Date;
  readonly modified?: Date;

  constructor(data: IWorkflowTransitionLogData) {
    super();

    // Validate data
    this.validate(data, ['id', 'user', 'transition']);

    this.id = data.id!;
    this.user = data.user!;
    this.transition = new WorkflowTransition(data.transition!);

    this.notes = data.notes ?? '';

    this.created =
      typeof data.created === 'string' ? new Date(data.created) : data.created!;

    this.modified =
      typeof data.modified === 'string'
        ? new Date(data.modified)
        : data.modified!;
  }
}

export class WorkflowTransitionAction extends AbstractModel
  implements IWorkflowTransitionAction {
  public readonly id?: number;
  public readonly name: string;
  public readonly description: string = '';
  public readonly action_type: WorkflowTransitionActionTypeEnum;
  public readonly email_config: null | IWorkflowTransitionActionEmail = null;

  constructor(data: IWorkflowTransitionActionData) {
    super();

    this.validate(data, ['name', 'action_type']);

    if (data.id) {
      this.id = data.id;
    }

    this.name = data.name!;

    this.action_type =
      data.action_type || WorkflowTransitionActionTypeEnum.SendEmail;

    if (data.description) {
      this.description = data.description;
    }

    if (data.email_config) {
      this.email_config = new WorkflowTransitionActionEmail(data.email_config);
    }
  }

  toString(): string {
    return `${this.name} - ${
      WorkflowTransitionActionTypeEnum[this.action_type]
    }`;
  }
}

export class WorkflowStateReminderConfig extends AbstractModel
  implements IWorkflowStateReminderConfig {
  readonly id?: number;
  readonly interval_in_days: number = 1;
  readonly email: IWorkflowStateReminderEmail | null = null;

  constructor(data: IWorkflowStateReminderConfigData) {
    super();

    if (data.id) {
      this.id = data.id;
    }

    if (data.interval_in_days) {
      this.interval_in_days = data.interval_in_days;
    }

    if (data.email) {
      this.email = new WorkflowStateReminderEmail(data.email);
    }
  }
}

export class WorkflowStateReminderEmail extends AbstractModel
  implements IWorkflowStateReminderEmail {
  readonly id?: number;
  readonly subject: string;
  readonly body: string;

  constructor(data: IWorkflowStateReminderEmailData) {
    super();

    this.validate(data, ['subject', 'body']);

    if (data.id) {
      this.id = data.id;
    }

    this.subject = data.subject!;

    this.body = data.body!;
  }

  toString() {
    return this.subject;
  }
}

export class WorkflowTransitionActionEmail extends AbstractModel
  implements IWorkflowTransitionActionEmail {
  public readonly id?: number;
  public readonly recipients: (
    | IWorkflowTransitionActionEmailRecipient
    | number
  )[] = [];
  public readonly subject: string;
  public readonly body: string;

  constructor(data: IWorkflowTransitionActionEmailData) {
    super();

    this.validate(data, ['subject', 'body']);

    if (data.id) {
      this.id = data.id;
    }

    if (data.recipients) {
      this.recipients = data.recipients.map(recipient =>
        typeof recipient === 'number'
          ? recipient
          : new WorkflowTransitionActionEmailRecipient(recipient)
      );
    }

    this.subject = data.subject!;
    this.body = data.body!;
  }

  toString(): string {
    return `${this.subject} - ${this.body}`;
  }
}

export class WorkflowTransitionActionEmailRecipient extends AbstractModel
  implements IWorkflowTransitionActionEmailRecipient {
  public readonly id?: number;
  public readonly email: string = '';
  public readonly recipient_type: TransitionActionRecipientTypeEnum =
    TransitionActionRecipientTypeEnum.Email;
  public readonly recipient_subtype: TransitionActionRecipientSubTypeEnum =
    TransitionActionRecipientSubTypeEnum.Direct;

  constructor(data: IWorkflowTransitionActionEmailRecipientData) {
    super();

    this.validate(data, []);

    if (data.id) {
      this.id = data.id;
    }

    if (data.email) {
      this.email = data.email;
    }

    if (data.recipient_type) {
      this.recipient_type = data.recipient_type;
    }
    if (data.recipient_subtype) {
      this.recipient_subtype = data.recipient_subtype;
    }
  }

  toString() {
    return `${this.email} - ${
      TransitionActionRecipientTypeEnum[this.recipient_type]
    }`;
  }
}

export class WorkflowStateThresholdGroup extends AbstractModel
  implements IWorkflowStateThresholdGroup {
  readonly id?: number;
  readonly name: string;
  readonly description: string = '';

  constructor(data: IWorkflowStateThresholdGroupData) {
    super();

    this.validate(data, ['name']);

    if (data.id) {
      this.id = data.id;
    }

    this.name = data.name!;

    if (data.description) {
      this.description = data.description;
    }
  }

  toString() {
    return this.name;
  }
}

export class WorkflowTransitionNode
  extends AbstractPredicateNode<
    TransitionNodeBinaryOperators | TransitionNodeOperandSubtype
  >
  implements IWorkflowTransitionNode {
  readonly id?: number;
  readonly variable_name: string;
  readonly groups: (Group | number)[] = [];
  readonly threshold_group: IWorkflowStateThresholdGroup | number | null = null;

  constructor(data: IWorkflowTransitionNodeData) {
    super(data);

    if (data.id) {
      this.id = data.id;
    }

    this.variable_name = data.variable_name ?? '';

    if (data.groups) {
      this.groups = data.groups.map(group =>
        typeof group === 'number' ? group : new Group(group)
      );
    }

    if (data.threshold_group) {
      this.threshold_group =
        typeof data.threshold_group === 'number'
          ? data.threshold_group
          : new WorkflowStateThresholdGroup(data.threshold_group);
    }
  }

  toString() {
    if (this.isOperator()) {
      switch (this.subtype) {
        case TransitionNodeBinaryOperators.StartBracket:
          return '(';
        case TransitionNodeBinaryOperators.CloseBracket:
          return ')';
        case TransitionNodeBinaryOperators.Equals:
          return '==';
        case TransitionNodeBinaryOperators.NotEqual:
          return '!=';
        case TransitionNodeBinaryOperators.GreaterThan:
          return '>';
        case TransitionNodeBinaryOperators.GreaterThanEqual:
          return '>=';
        case TransitionNodeBinaryOperators.LessThan:
          return '<';
        case TransitionNodeBinaryOperators.LessThanEqual:
          return '<=';
        case TransitionNodeBinaryOperators.And:
          return '&&';
        case TransitionNodeBinaryOperators.Or:
          return '||';
        case TransitionNodeBinaryOperators.Not:
          return '!';
        default:
          return `Unknown(${this.subtype})`;
      }
    } else {
      const hashOperandValue: Record<TransitionNodeOperandSubtype, string> = {
        [TransitionNodeOperandSubtype.Constant]: `${this.constant}`,
        [TransitionNodeOperandSubtype.OwnerGroup]: `Owner Group == (${this.groups
          .map(group => group.toString())
          .join(', ')})`,
        [TransitionNodeOperandSubtype.AssigneeGroup]: `Assignee Group == (${this.groups
          .map(group => group.toString())
          .join(', ')})`,
        [TransitionNodeOperandSubtype.Threshold]: `Threshold == ${this.threshold_group?.toString()}`,
        [TransitionNodeOperandSubtype.Variable]: `${this.variable_name}`
      };

      return (
        hashOperandValue[this.subtype as TransitionNodeOperandSubtype] ||
        `Unknown(${this.subtype})`
      );
    }
  }
}
